import React from 'react'
import {firstVisibleDay, lastVisibleDay} from 'react-big-calendar/lib/utils/dates'
import moment from 'moment'
import gql from 'graphql-tag'
import {Mutation} from 'react-apollo'
import {Modal, Checkbox, Radio, InputNumber, notification} from 'antd'
import {momentRange} from 'lib/dateHelpers'
import Query from 'shared/components/Query'
import Layout from 'components/PrivateRoutes/Layout'
import {QueryParams, DateTimeRange, Calendar} from 'components/shared'
import { getBookingState } from 'lib/bookingStateHelper'

import './index.css'

export const AvailabilityAndBookingQuery = gql`
  query AvailabilityAndBookingQuery($after: DateTime, $before: DateTime) {
    serviceProvider {
      id
      availability(endsAfterDateTime: $after, startsBeforeDateTime: $before) {
        edges {
          node {
            id
            startsAt
            endsAt
            availability
          }
        }
      }
      bookings(endsAfterDateTime: $after, startsBeforeDateTime: $before) {
        edges {
          node {
            id
            startsAt
            endsAt
            state
            products {
              edges {
                node {
                  id
                  name
                  state
                }
              }
            }
          }
        }
      }
    }
  }
`

const UpdateAvailabilityMutation = gql`
  mutation UpdateAvailability($input: UpdateAvailabilityInput!) {
    UpdateAvailability(input: $input) {
      clientMutationId
    }
  }
`

class Availability extends React.Component {
  state = {
    events: [],
    formIsOpen: false,
    form: {
      weeksToRepeat: 0,
      availability: 'available',
      start: null,
      end: null,
    },
  }

  onSelectSlot = ({availability = 'available', start, end}) => {
    this.setState({
      formIsOpen: true,
      form: {
        ...this.state.form,
        start,
        end,
        availability,
      }
    })
  }

  updateAvailability = (mutation) => {
    const {
      start: startAt,
      end: endAt,
      availability,
      weeksToRepeat,
    } = this.state.form

    const maxWeeksRange = weeksToRepeat || 1
    const dates = []

    // Loop through each week at a time
    for (let weekIndex = 0; weekIndex < maxWeeksRange; weekIndex++) {
      const start = moment(startAt)
      const end = moment(endAt)

      if (weekIndex > 0) {
        start.add(weekIndex, 'weeks')
        end.add(weekIndex, 'weeks')
      }

      // Add availability for each weekday
      momentRange(start, end).forEach(date => {
        dates.push({
          startsAt: date.toDate(),
          endsAt: date
            .set({hour: end.get('hour'), minute: end.get('minute')})
            .toDate(),
        })
      })
    }

    return mutation({
      variables: {
        input: {
          availability,
          dates,
        },
      },
    })
  }

  closeModal = () => {
    this.setState({
      formIsOpen: false,
      form: {
        weeksToRepeat: 0,
        availability: 'available',
        start: null,
        end: null,
      }
    })
  }

  updateForm = (data) => {
    this.setState({
      form: {
        ...this.state.form,
        ...data,
      }
    })
  }

  submitSuccessful = (refetch) => {
    refetch()
    this.closeModal()
    notification['success']({
      message: 'Tilgjengelighet oppdatert',
      description: 'Din tilgjengelighet ble lagret og er nå oppdatert i kalenderen under.',
    })
  }

  submitError = (err) => {
    notification['error']({
      message: 'Kunne ikke lagre',
      description: err && err.message
        ? err.message
        : 'Kunne ikke lagre din tilgjengelighet grunnet en feil. Forsøk på nytt senere eller kontakt EFKT kundeservice.',
    })
  }

  formContent = ({refetch}) => {
    const form = this.state.form
    const weeksToRepeatIsChecked = form.weeksToRepeat > 0
    return (
      <Mutation
        mutation={UpdateAvailabilityMutation}
        onCompleted={this.submitSuccessful.bind(this, refetch)}
        onError={this.submitError}
      >
        {(updateAvailability) => (
          <Modal
            className="availability"
            visible={true}
            onOk={this.updateAvailability.bind(this, updateAvailability)}
            onCancel={this.closeModal}
          >
            <div className="availability-mode">
              <Radio.Group
                buttonStyle="solid"
                value={form.availability}
                onChange={(e) => this.updateForm({availability: e.target.value})}
              >
                <Radio.Button value="available">Tilgjengelig</Radio.Button>
                <Radio.Button value="maybe">Kanskje tilgjengelig</Radio.Button>
                <Radio.Button value="unavailable">Utilgjengelig</Radio.Button>
              </Radio.Group>
            </div>

            <DateTimeRange
              start={moment(form.start)}
              end={moment(form.end)}
              onChange={this.updateForm}
            />

            <div className="repeat">
              <Checkbox
                checked={weeksToRepeatIsChecked}
                onChange={(e) =>
                  this.updateForm({
                    weeksToRepeat: e.target.checked ? 4 : 0
                  })
                }
              >
                Gjenta
              </Checkbox>

              {weeksToRepeatIsChecked && (
                <React.Fragment>
                  uker:
                  <InputNumber
                    size="small"
                    className="week-input"
                    min={1}
                    max={52}
                    value={form.weeksToRepeat}
                    onChange={weeksToRepeat => this.updateForm({weeksToRepeat})}
                  />
                </React.Fragment>
              )}
            </div>
          </Modal>
        )}
      </Mutation>
    )
  }

  render() {
    const {
      formIsOpen,
    } = this.state

    return (
      <Layout {...this.props} title="Tilgjengelighet">
        <QueryParams>
          {({getQueryParam, setQueryParams}) => {
            const date = moment(
              getQueryParam('date', moment().format())
            )

            const after = firstVisibleDay(date.toDate(), Calendar.localizer)
            const before = lastVisibleDay(date.toDate(), Calendar.localizer)

            return (
              <Query
                query={AvailabilityAndBookingQuery}
                variables={{
                  after,
                  before,
                }}
              >
                {({getData, refetch}) => {
                  const availability = getData('serviceProvider.availability.edges', [])
                  const booking = getData('serviceProvider.bookings.edges', [])

                  const availabilities = availability.map(({node}) => {
                    const start = moment(node.startsAt)
                    const end = moment(node.endsAt)
                    const availabilityLabel = node.availability === 'maybe'
                      ? 'Kanskje tilgjengelig'
                      : 'Tilgjengelig'
                    return {
                      id: node.id,
                      start: start.toDate(),
                      end: end.toDate(),
                      title: `${availabilityLabel} ${start.format('HH:mm')} - ${end.format('HH:mm')}`,
                      availability: node.availability,
                    }
                  })

                  const bookings = booking.map(({node}) => {
                    const start = moment(node.startsAt)
                    const end = moment(node.endsAt)
                    return {
                      id: node.id,
                      start: start.toDate(),
                      end: end.toDate(),
                      title: `Booking: ${start.format('HH:mm')} - ${end.format('HH:mm')}`,
                      isBooking: true,
                      state: getBookingState(node),
                    }
                  })

                  const events = [...availabilities, ...bookings]

                  return (
                    <React.Fragment>
                      <Calendar
                        selectable="ignoreEvents"
                        view={getQueryParam('view', 'week')}
                        views={['month', 'week']}
                        date={date.toDate()}
                        onView={view => setQueryParams({view})}
                        onNavigate={date => setQueryParams({date: moment(date).format()})}
                        onSelectEvent={(event) => {
                          if (event.isBooking) {
                            return window.open(`/bookings/${event.id}`)
                          }
                          this.onSelectSlot(event)
                        }}
                        onSelectSlot={this.onSelectSlot}
                        events={events}
                        eventPropGetter={(event) => {
                          if (event.availability === 'maybe') {
                            return {className: 'calendar-event-maybe'}
                          }
                          if (event.isBooking) {
                            return {
                              className: `calendar-event-booking ${event.state}`
                            }
                          }
                        }}
                      />

                      {!!formIsOpen && this.formContent({refetch})}
                    </React.Fragment>
                  )
                }}
              </Query>
            )
          }}
        </QueryParams>
      </Layout>
    )
  }

}

export default Availability
